/**
 * Map View Events - Event listener setup for map view
 *
 * Extracted from navigateToMapView() to reduce function length.
 * Sets up all document-level event listeners for map interactions.
 */

import { HexEvents } from './hex-events.js';

/**
 * Get left sidebar element
 * @returns {HTMLElement|null}
 */
function getLeftSidebarElement() {
  return document.getElementById('left-sidebar');
}

/**
 * Wire up hex interaction events (click, map-click, state changes)
 * @param {Object} params - Parameters
 * @param {boolean} params.isPlayerView - Whether this is player view
 * @param {Object} params.gmChannel - BroadcastChannel for GM-Player sync
 * @param {Function} params.markSessionModified - Function to mark session as modified
 * @param {Function} params.broadcastSessionState - Function to broadcast state
 */
export function wireHexEvents({ isPlayerView, gmChannel, markSessionModified, broadcastSessionState }) {
  // Listen for hex-clicked events to update the left sidebar
  document.addEventListener('hex-clicked', (event) => {
    const leftSidebar = getLeftSidebarElement();
    if (leftSidebar && leftSidebar.selectHex) {
      leftSidebar.selectHex(event.detail);
    }

    // Store selected hex in session (for sync to Player View)
    if (!isPlayerView && window.currentSession) {
      window.currentSession.selectedHex = event.detail;
    }

    // Broadcast hex selection to Player View
    if (!isPlayerView && gmChannel) {
      gmChannel.postMessage({
        type: 'hex-selection',
        hexData: event.detail
      });
    }
  });

  // Listen for map-clicked (empty area) to deselect hex in sidebar
  document.addEventListener('map-clicked', () => {
    const leftSidebar = getLeftSidebarElement();
    if (leftSidebar && leftSidebar.deselectHex) {
      leftSidebar.deselectHex();
    }

    // Clear selected hex in session (for sync to Player View)
    if (!isPlayerView && window.currentSession) {
      window.currentSession.selectedHex = null;
    }

    // Broadcast deselection to Player View
    if (!isPlayerView && gmChannel) {
      gmChannel.postMessage({
        type: 'hex-deselection'
      });
    }
  });

  // Listen for hex state changes (fog of war) to broadcast to Player View
  document.addEventListener(HexEvents.HEX_STATE_CHANGED, () => {
    // Mark session as modified and update undo/redo buttons
    markSessionModified(true);

    // Broadcast session state to Player View
    broadcastSessionState();
  });
}

/**
 * Wire up icon picker events
 * @param {Function} markSessionModified - Function to mark session as modified
 * @param {Function} broadcastSessionState - Function to broadcast state
 * @param {Function} ensureHexState - Function to ensure hex state exists
 */
export function wireIconEvents({ markSessionModified, broadcastSessionState, ensureHexState }) {
  const iconPicker = document.getElementById('icon-picker');
  if (!iconPicker) return;

  iconPicker.addEventListener('icon-saved', (event) => {
    const { hexCoord, icon, isEdit } = event.detail;
    const hexState = ensureHexState(hexCoord);
    if (!hexState) return;

    if (isEdit) {
      // Update existing icon
      const index = hexState.icons.findIndex(i => i.id === icon.id);
      if (index !== -1) {
        hexState.icons[index] = icon;
      }
    } else {
      // Add new icon
      hexState.icons.push(icon);
    }

    // Update the left sidebar display
    const leftSidebar = getLeftSidebarElement();
    if (leftSidebar && leftSidebar.displayIcons) {
      leftSidebar.displayIcons();
    }

    // Refresh icons on the map
    const hexMap = document.getElementById('main-map');
    if (hexMap && hexMap.refreshIcons) {
      hexMap.refreshIcons();
    }

    // Mark session as modified
    markSessionModified();

    // Broadcast session state to Player View
    broadcastSessionState();
  });

  iconPicker.addEventListener('icon-deleted', (event) => {
    const { hexCoord, iconId } = event.detail;
    const hexState = ensureHexState(hexCoord);
    if (!hexState) return;

    // Remove icon from state
    hexState.icons = hexState.icons.filter(i => i.id !== iconId);

    // Update the left sidebar display
    const leftSidebar = getLeftSidebarElement();
    if (leftSidebar && leftSidebar.displayIcons) {
      leftSidebar.displayIcons();
    }

    // Refresh icons on the map
    const hexMap = document.getElementById('main-map');
    if (hexMap && hexMap.refreshIcons) {
      hexMap.refreshIcons();
    }

    // Mark session as modified
    markSessionModified();

    // Broadcast session state to Player View
    broadcastSessionState();
  });
}

/**
 * Wire up note editor events
 * @param {Function} markSessionModified - Function to mark session as modified
 * @param {Function} broadcastSessionState - Function to broadcast state
 * @param {Function} ensureHexState - Function to ensure hex state exists
 */
export function wireNoteEvents({ markSessionModified, broadcastSessionState, ensureHexState }) {
  const noteEditor = document.getElementById('note-editor');
  if (!noteEditor) return;

  noteEditor.addEventListener('note-saved', (event) => {
    const { hexCoord, note, isEdit } = event.detail;
    const hexState = ensureHexState(hexCoord);
    if (!hexState) return;

    if (isEdit) {
      // Update existing note
      const index = hexState.notes.findIndex(n => n.id === note.id);
      if (index !== -1) {
        hexState.notes[index] = note;
      }
    } else {
      // Add new note
      hexState.notes.push(note);
    }

    // Update the left sidebar display
    const leftSidebar = getLeftSidebarElement();
    if (leftSidebar && leftSidebar.displayNotes) {
      leftSidebar.displayNotes();
    }

    // Mark session as modified
    markSessionModified();

    // Broadcast session state to Player View
    broadcastSessionState();
  });

  noteEditor.addEventListener('note-deleted', (event) => {
    const { hexCoord, noteId } = event.detail;
    const hexState = ensureHexState(hexCoord);
    if (!hexState) return;

    // Remove note from state
    hexState.notes = hexState.notes.filter(n => n.id !== noteId);

    // Update the left sidebar display
    const leftSidebar = getLeftSidebarElement();
    if (leftSidebar && leftSidebar.displayNotes) {
      leftSidebar.displayNotes();
    }

    // Mark session as modified
    markSessionModified();

    // Broadcast session state to Player View
    broadcastSessionState();
  });
}

/**
 * Wire up token events
 * @param {Function} markSessionModified - Function to mark session as modified
 * @param {Function} broadcastSessionState - Function to broadcast state
 * @param {Function} getHexMapElement - Function to get hex map element
 */
export function wireTokenEvents({ markSessionModified, broadcastSessionState, getHexMapElement }) {
  // Handle create-token event from sidebar
  document.addEventListener('create-token', () => {
    const tokenCreator = document.getElementById('token-creator');
    if (tokenCreator) {
      tokenCreator.open();
    }
  });

  // Handle edit-token event from sidebar
  document.addEventListener('edit-token', (event) => {
    const tokenCreator = document.getElementById('token-creator');
    if (tokenCreator) {
      tokenCreator.open(event.detail.token);
    }
  });

  // Handle token deletion
  document.addEventListener('token-deleted', (event) => {
    if (!window.currentSession) return;

    const { tokenId } = event.detail;

    // Remove token from session
    if (window.currentSession.tokens) {
      window.currentSession.tokens = window.currentSession.tokens.filter(t => t.id !== tokenId);
    }

    // Update the left sidebar display
    const leftSidebar = getLeftSidebarElement();
    if (leftSidebar && leftSidebar.displayTokens) {
      leftSidebar.displayTokens();
    }

    // Update the hex map to remove token visuals
    const hexMap = getHexMapElement('gm');
    if (hexMap && hexMap.removeToken) {
      hexMap.removeToken(tokenId);
    }

    // Mark session as modified
    markSessionModified();

    // Broadcast session state to Player View
    broadcastSessionState();
  });

  // Wire up token creator events
  const tokenCreator = document.getElementById('token-creator');
  if (tokenCreator) {
    tokenCreator.addEventListener('token-created', (event) => {
      if (!window.currentSession) return;

      const tokenData = event.detail;

      // Create token with unique ID
      const token = {
        id: `token-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
        name: tokenData.name,
        color: tokenData.color,
        visibility: tokenData.visibility,
        imageDataUrl: tokenData.imageDataUrl,
        positionHistory: [], // Position history with game dates
        createdAt: tokenData.createdAt
      };

      // Add to session tokens array
      if (!window.currentSession.tokens) {
        window.currentSession.tokens = [];
      }
      window.currentSession.tokens.push(token);

      // Update the left sidebar display
      const leftSidebar = getLeftSidebarElement();
      if (leftSidebar && leftSidebar.displayTokens) {
        leftSidebar.displayTokens();
      }

      // Mark session as modified
      markSessionModified();

      // Broadcast session state to Player View
      broadcastSessionState();
    });

    tokenCreator.addEventListener('token-edited', (event) => {
      if (!window.currentSession) return;

      const { tokenId, tokenData } = event.detail;

      // Find and update the token
      const token = window.currentSession.tokens?.find(t => t.id === tokenId);
      if (token) {
        token.name = tokenData.name;
        token.color = tokenData.color;
        token.visibility = tokenData.visibility;
        token.imageDataUrl = tokenData.imageDataUrl;
        // Keep existing id, position, and createdAt

        // Re-render token on map if it's placed
        if (token.position) {
          const hexMap = getHexMapElement('gm');
          if (hexMap && hexMap.renderAllTokens) {
            hexMap.renderAllTokens();
          }
        }

        // Update the left sidebar display
        const leftSidebar = getLeftSidebarElement();
        if (leftSidebar && leftSidebar.displayTokens) {
          leftSidebar.displayTokens();
        }
      }

      // Mark session as modified
      markSessionModified();

      // Broadcast session state to Player View
      broadcastSessionState();
    });
  }

  // Handle token placement on map
  document.addEventListener('token-placed', (event) => {
    // Update sidebar to show token's new position
    const leftSidebar = getLeftSidebarElement();
    if (leftSidebar && leftSidebar.displayTokens) {
      leftSidebar.displayTokens();
    }

    // Clear placement mode
    window.selectedTokenForPlacement = null;

    // Mark session as modified
    markSessionModified();

    // Broadcast session state to Player View
    broadcastSessionState();
  });

  // Handle token selection (click-to-place mode)
  document.addEventListener('token-selected', (event) => {
    window.selectedTokenForPlacement = event.detail.tokenId;
  });

  document.addEventListener('token-deselected', () => {
    window.selectedTokenForPlacement = null;
  });
}

/**
 * Wire up keyboard shortcuts
 * @param {Object} params - Parameters
 * @param {Function} params.handleSaveSession - Save session handler
 * @param {Function} params.handleUndo - Undo handler
 * @param {Function} params.handleRedo - Redo handler
 * @param {boolean} params.isPlayerView - Whether this is player view
 */
export function wireKeyboardShortcuts({ handleSaveSession, handleUndo, handleRedo, isPlayerView }) {
  document.addEventListener('keydown', (event) => {
    // Ctrl+S / Cmd+S: Save
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault();
      if (!isPlayerView) {
        handleSaveSession();
      }
    }

    // Ctrl+Z / Cmd+Z: Undo
    if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
      event.preventDefault();
      if (!isPlayerView) {
        handleUndo();
      }
    }

    // Ctrl+Y / Cmd+Shift+Z: Redo
    if (((event.ctrlKey || event.metaKey) && event.key === 'y') ||
        ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'z')) {
      event.preventDefault();
      if (!isPlayerView) {
        handleRedo();
      }
    }

    // D key: Toggle debug panel
    if (event.key === 'd' && !event.ctrlKey && !event.metaKey) {
      const debugPanel = document.querySelector('debug-panel');
      if (debugPanel && debugPanel.toggle) {
        debugPanel.toggle();
      }
    }

    // Escape: Deselect hex
    if (event.key === 'Escape') {
      const leftSidebar = getLeftSidebarElement();
      if (leftSidebar && leftSidebar.deselectHex) {
        leftSidebar.deselectHex();
      }

      // Clear selection highlight on map
      const hexMap = document.querySelector('hex-map');
      if (hexMap && hexMap.clearSelectionHighlight) {
        hexMap.clearSelectionHighlight();
      }
    }
  });
}

/**
 * Wire up toast notifications
 * @param {Function} showToast - Function to show toast notifications
 */
export function wireToastNotifications(showToast) {
  document.addEventListener('show-toast', (event) => {
    showToast(event.detail.message, event.detail.duration);
  });
}
